import { useFormBehaviors } from 'hooks';
import { UseFormBehaviors } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { useTranslation } from 'hooks/useTranslation';
import { useFetchAssignedGroupedPositionsContext } from 'pages/Settings/pages/LocationSettings/PositionsAllocation/FetchAssignedGroupedPositions.data.context';
import { usePositionsAllocationSettingsPageDataContext } from 'pages/Settings/pages/LocationSettings/PositionsAllocation/PositionsAllocationSettingsPage.data.context';
import { PositionsAllocationSettingsPageFormValues as ALSPFFields } from 'pages/Settings/pages/LocationSettings/PositionsAllocation/PositionsAllocationSettingsPageForm.types';
import { createContext, FC, FormEvent, useCallback, useContext, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { API_POST_updateCustomerPositionsAllocation } from 'requests/POST_updatePositionsAllocation';
import { toastService } from 'services';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';

export type PositionsAllocationSettingsPageFormContextType = UseFormBehaviors<ALSPFFields, {}> & {
  chargeRateErrors: Record<string, string[]>;
};

const PositionsAllocationSettingsPageFormContext =
  createContext<PositionsAllocationSettingsPageFormContextType>(
    {} as PositionsAllocationSettingsPageFormContextType,
  );

export const PositionsAllocationSettingsPageFormProvider: FC = ({ children }) => {
  const { data, fetch } = usePositionsAllocationSettingsPageDataContext();
  const hasMarketplace = !!data?.fetchCommunity?.hasMarketplace;
  const { data: groupedPositionsData, refetch } = useFetchAssignedGroupedPositionsContext();
  const { locationId } = useParams<{ locationId: string }>();
  const { apiRequest } = useAPIActions();
  const history = useHistory();
  const { t } = useTranslation();
  const existingPositionIds = useMemo(
    () => data?.fetchCommunity?.locationPositions.map((position) => position.id) || [],
    [data],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const initialState = groupedPositionsData?.groupPositions?.edges?.reduce((acc: any, group) => {
    group.node?.items &&
      group.node.items.forEach((position) => {
        const isAssigned = data?.fetchCommunity?.locationPositions?.some(
          (locPosition) => locPosition.id === position.id,
        );
        acc[`positionLocation_${position.id}`] = isAssigned;

        // Set supervisor value for existing positions
        if (isAssigned) {
          const existingPosition = data?.fetchCommunity?.locationPositions?.find(
            (locPosition) => locPosition.id === position.id,
          );
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          acc[`supervisor_${position.id}`] = (existingPosition as any)?.supervisor || false;
        } else {
          acc[`supervisor_${position.id}`] = false;
        }
      });
    return acc;
  }, {});

  const form = useFormBehaviors<ALSPFFields, {}>({
    initialState: { ...initialState },
    onSubmit: async (formValues: ALSPFFields) => {
      // Get all position IDs that are present in form values (both positionLocation and supervisor)
      const positionIdsInForm = new Set<number>();

      Object.entries(formValues).forEach(([key, value]) => {
        if (
          key.startsWith('positionLocation_') ||
          key.startsWith('supervisor_') ||
          key.startsWith('chargeRate_')
        ) {
          const positionId = parseInt(
            key.replace(/^(positionLocation_|supervisor_|chargeRate_)/, ''),
            10,
          );
          positionIdsInForm.add(positionId);
        }
      });
      const getCommunitiesPositionId = (positionId: number) => {
        const communityPosition = data?.fetchCommunity?.communitiesPositions?.find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (pos: any) => pos.position.displayId === positionId,
        );
        return communityPosition?.id;
      };

      const existingPositionIdsAsNumbers = existingPositionIds.map((id) => parseInt(`${id}`, 10));

      // Get supervisor values for positions in form
      const supervisorValues = Object.entries(formValues)
        .filter(([key, value]) => key.startsWith('supervisor_'))
        .reduce((acc, [key, value]) => {
          const positionId = parseInt(key.replace('supervisor_', ''), 10);
          acc[positionId] = value === true;
          return acc;
        }, {} as Record<number, boolean>);

      const chargeRateValues = Object.entries(formValues)
        .filter(([key, value]) => key.startsWith('chargeRate_'))
        .reduce((acc, [key, value]) => {
          const positionId = parseInt(key.replace('chargeRate_', ''), 10);
          acc[positionId] = value;
          return acc;
        }, {} as Record<number, number>);

      // Get position assignment values
      const positionAssignmentValues = Object.entries(formValues)
        .filter(([key, value]) => key.startsWith('positionLocation_'))
        .reduce((acc, [key, value]) => {
          const positionId = parseInt(key.replace('positionLocation_', ''), 10);
          acc[positionId] = value === true;
          return acc;
        }, {} as Record<number, boolean>);

      // Build communities_positions_attributes array only for positions in form
      const communitiesPositionsAttributes: Array<{
        id?: number;
        position_id?: number;
        supervisor: boolean;
        charge_rate?: number;
      }> = [];

      positionIdsInForm.forEach((positionId) => {
        const isAssigned = positionAssignmentValues[positionId] || false;
        const isExistingPosition = existingPositionIdsAsNumbers.includes(positionId);
        const supervisorValue = supervisorValues[positionId] || false;
        const chargeRateValue = chargeRateValues[positionId] || null;
        const shouldIncludeChargeRate = hasMarketplace && chargeRateValue !== null;

        if (isAssigned) {
          if (isExistingPosition) {
            const communityPositionId = getCommunitiesPositionId(positionId);
            if (communityPositionId) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const item: any = {
                id: communityPositionId,
                supervisor: supervisorValue,
              };
              if (shouldIncludeChargeRate) item.charge_rate = chargeRateValue;
              communitiesPositionsAttributes.push(item);
            }
          } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const item: any = {
              position_id: positionId,
              supervisor: supervisorValue,
            };
            if (shouldIncludeChargeRate) item.charge_rate = chargeRateValue;
            communitiesPositionsAttributes.push(item);
          }
        } else if (isExistingPosition) {
          // Existing position but not assigned in form - still include if supervisor changed
          // This handles the case where only supervisor flag is being modified
          const communityPositionId = getCommunitiesPositionId(positionId);
          if (communityPositionId) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const item: any = {
              id: communityPositionId,
              supervisor: supervisorValue,
            };
            if (shouldIncludeChargeRate) item.charge_rate = chargeRateValue;
            communitiesPositionsAttributes.push(item);
          }
        }
      });

      const payload = {
        community: {
          communities_positions_attributes: communitiesPositionsAttributes,
        },
      };
      const result = await apiRequest(
        API_POST_updateCustomerPositionsAllocation(locationId, payload),
        {
          onSuccess: () => {
            fetch();
            refetch && refetch();
            history.push(`/settings/locations/${locationId}/positions-allocation`);
            toastService.success(`${t('success:POSITIONS_SUCCESSFULLY_ALLOCATED')}`);
          },
          onError: (e) =>
            handleGenericErrorMessage(e, t('errors:FAILED_TO_UPDATE_POSITIONS_ALLOCATION')),
        },
      );
      return result;
    },
    validations: {},
  });

  const chargeRateErrorMessage = t('location_settings:CHARGE_RATE_MUST_BE_GREATER_THAN_ZERO');

  const chargeRateErrors = useMemo(() => {
    const errors: Record<string, string[]> = {};
    if (!hasMarketplace) return errors;

    const assignedPositionIds = new Set(existingPositionIds);

    groupedPositionsData?.groupPositions?.edges?.forEach((group) => {
      group.node?.items?.forEach((position) => {
        const isAppliedNow = form.values[`positionLocation_${position.id}`] === true;
        if (!isAppliedNow) return;

        const rawChargeRate = form.values[`chargeRate_${position.id}`];
        const isNewlyApplied = !assignedPositionIds.has(position.id);
        const isRateChanged = rawChargeRate !== undefined;
        // Only enforce on positions being applied or edited
        if (!isNewlyApplied && !isRateChanged) return;

        const effectiveChargeRate =
          rawChargeRate ?? (position.chargeRate != null ? String(position.chargeRate) : '');
        const parsedChargeRate = parseFloat(effectiveChargeRate);
        if (effectiveChargeRate === '' || Number.isNaN(parsedChargeRate) || parsedChargeRate <= 0) {
          errors[`${position.id}`] = [chargeRateErrorMessage];
        }
      });
    });

    return errors;
  }, [
    hasMarketplace,
    existingPositionIds,
    groupedPositionsData,
    form.values,
    chargeRateErrorMessage,
  ]);

  const hasChargeRateErrors = Object.keys(chargeRateErrors).length > 0;

  const isDisabled = useMemo(() => {
    if (hasChargeRateErrors) return true;
    return Object.entries(form?.values ?? {}).every(([key, value]) => {
      if (
        key?.startsWith?.('positionLocation_') ||
        key?.startsWith?.('supervisor_') ||
        key?.startsWith?.('chargeRate_')
      ) {
        return value === initialState?.[key];
      }
      return true;
    });
  }, [form?.values, initialState, hasChargeRateErrors]);

  // Guard the submit itself: a disabled Save button does not prevent Enter-key form submission,
  // and useFormBehaviors does not re-run validations on submit.
  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      if (hasChargeRateErrors) {
        event.preventDefault();
        toastService.error(chargeRateErrorMessage);
        return;
      }
      return form.onSubmit(event);
    },
    [hasChargeRateErrors, form, chargeRateErrorMessage],
  );

  return (
    <PositionsAllocationSettingsPageFormContext.Provider
      value={{ ...form, isDisabled, onSubmit, chargeRateErrors }}
    >
      {children}
    </PositionsAllocationSettingsPageFormContext.Provider>
  );
};

export const usePositionsAllocationSettingsPageFormContext = () =>
  useContext(PositionsAllocationSettingsPageFormContext);
